import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useBts } from "../../lib/queries";
import { mediaUrl, isVideoFile, isYouTube, isVimeo } from "../../lib/media";
import { optimizeImg } from "../../lib/img";

export default function BTS() {
  const { data: bts = [] } = useBts();
  const [openIdx, setOpenIdx] = useState(null);
  const { t } = useLang();

  const next = () => setOpenIdx((i) => (i + 1) % bts.length);
  const prev = () => setOpenIdx((i) => (i - 1 + bts.length) % bts.length);

  return (
    <section id="bts" data-testid="bts-section" className="py-32 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-px bg-[#A855F7]" />
          <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
            {t("bts_kicker")}
          </span>
        </div>
        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white max-w-3xl">
          {t("bts_title")}
        </h2>
      </div>

      <div className="flex gap-4 md:gap-6 px-6 lg:px-10 pb-8 overflow-x-auto snap-x snap-mandatory scrollbar-none">
        {bts.map((b, i) => (
          <motion.button
            key={b.id}
            data-testid={`bts-${b.id}`}
            onClick={() => setOpenIdx(i)}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.05 }}
            className="relative flex-shrink-0 snap-start w-[280px] md:w-[380px] aspect-[3/4] rounded-2xl overflow-hidden border border-white/5 hover:border-[#A855F7]/50 group transition-colors"
          >
            <img
              src={optimizeImg(b.media_url, 600, 72)}
              alt={b.caption_tr}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080D] via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="font-mono-ui text-[9px] tracking-widest text-[#A855F7] uppercase mb-1">
                Frame {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-white text-sm">{b.caption_tr}</div>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex items-center justify-center"
            onClick={() => setOpenIdx(null)}
            data-testid="lightbox"
          >
            <button
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); setOpenIdx(null); }}
              className="absolute top-6 right-6 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#A855F7] hover:text-[#A855F7]"
            >
              <X size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 md:left-10 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#A855F7] hover:text-[#A855F7]"
            >
              <ChevronLeft size={20} />
            </button>
            <motion.img
              key={openIdx}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              src={mediaUrl(bts[openIdx].media_url)}
              alt=""
              className="max-h-[85vh] max-w-[85vw] rounded-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 md:right-10 w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#A855F7] hover:text-[#A855F7]"
            >
              <ChevronRight size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
